import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import { media } from "@atlaskit/primitives/responsive";

import FeatureHighlight from "@/components/wpl/feature-highlight/src";
import { DEFAULT_VALUES } from "@/libraries/web-framework";
import {
    getViewRepositoriesSectionImage,
    getFeatureCoordinateSectionImage,
    getFeatureConnectWorkSectionImage
} from "@/templates/jira-software-features-page/src/localized-images";

const containerStyles = css({
    paddingBlockStart: "16px",
    background: "#091e42",

    h2: {
        paddingBlockStart: "0",
        margin: "0 auto",
        [media.below.sm]: {
            fontSize: "1.5rem",
            width: "100%"
        },
        [media.only.sm]: {
            fontSize: "2.25rem",
            width: "66.66666%"
        },
        [media.above.md]: {
            fontSize: "2.25rem",
            width: "50%"
        }
    },
    h6: {
        fontSize: "0.75rem",
        marginBlockStart: "0",
        padding: "16px 0 0",
        [media.below.md]: {
            marginBlockEnd: "10px"
        },
        [media.above.md]: {
            marginBlockEnd: "16px"
        }
    }
});

const FeaturesSection = () => {
    const intl = useIntl();
    const header = intl.formatMessage(
        {
            id: "JiraSoftwareFeaturesPageTemplate.FeaturesSection.header",
            defaultMessage:
                "See code and deployment status at a glance in Jira "
        },
        { ...DEFAULT_VALUES }
    );
    const subtitle = intl.formatMessage(
        {
            id: "JiraSoftwareFeaturesPageTemplate.FeaturesSection.subtitle",
            defaultMessage: "Let developers focus on code"
        },
        { ...DEFAULT_VALUES }
    );
    const viewRepositoriesLocalizedImageSrc = getViewRepositoriesSectionImage(
        intl.locale
    );
    const featureCoordinateLocalizedImageSrc = getFeatureCoordinateSectionImage(
        intl.locale
    );
    const featureConnectWorkLocalizedImageSrc =
        getFeatureConnectWorkSectionImage(intl.locale);

    const featureCards = [
        {
            title: intl.formatMessage({
                id: "JiraSoftwareFeaturesPageTemplate.FeaturesSection.view-repositories-title",
                defaultMessage: "View repositories"
            }),
            body: intl.formatMessage({
                id: "JiraSoftwareFeaturesPageTemplate.FeaturesSection.view-repositories-body",
                defaultMessage:
                    "Include an issue key in a commit, branch name, or PR and it will automatically update in Jira."
            }),
            contentSrc: viewRepositoriesLocalizedImageSrc
        },
        {
            title: intl.formatMessage({
                id: "JiraSoftwareFeaturesPageTemplate.FeaturesSection.coordinate-title",
                defaultMessage: "See development status"
            }),
            body: intl.formatMessage({
                id: "JiraSoftwareFeaturesPageTemplate.FeaturesSection.coordinate-body",
                defaultMessage:
                    "View and create branches, pull requests and view commits right inside the Jira issue development panel. "
            }),
            contentSrc: featureCoordinateLocalizedImageSrc
        },
        {
            title: intl.formatMessage({
                id: "JiraSoftwareFeaturesPageTemplate.FeaturesSection.connect-work-title",
                defaultMessage: "Get DevOps visibility"
            }),
            body: intl.formatMessage({
                id: "JiraSoftwareFeaturesPageTemplate.FeaturesSection.connect-work-body",
                defaultMessage:
                    "Visualize and measure progress from idea through to production across all tools. "
            }),
            contentSrc: featureConnectWorkLocalizedImageSrc
        }
    ];

    return (
        <div css={containerStyles}>
            <FeatureHighlight
                subtitle={subtitle as string}
                header={header as string}
                background="darkblue"
                mediaType="image"
                transparentActiveBackground={true}
                cards={featureCards}
            />
        </div>
    );
};

export default FeaturesSection;
